import { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  Tooltip, XAxis, YAxis, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarCheck, faWallet, faBed, faArrowRightToBracket,
  faArrowRightFromBracket, faXmark, faSearch, faDownload,
  faFilter, faPlus, faPen, faTrash, faCheck, faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import DashboardLayout from "../layout/DashboardLayout";
import { breakdownFromTotal } from "../../utils/pricing";
import { usePricingConfig } from "../../utils/usePricingConfig";
import "../layout/Dashboard.css";
import "./BookingsPage.css";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

// ── Types ────────────────────────────────────────────────────
interface Booking {
  id: string;
  bookingId: string;
  roomId: string;
  allRoomIds?: string[];
  allRoomNumbers?: string[];
  guestName: string;
  phone: string;
  checkIn: string | null;
  checkOut: string | null;
  nights: number;
  total: number;
  status: string;
  bookingRoomStatus?: string | null;
  effectiveStatus?: string;
  createdAt: string | null;
  season: string | null;
  guestCount: number;
  childrenCount?: number;
  dayUse?: boolean;
  bookingType?: string | null;
  source?: string | null;
  notes?: string | null;
  cancellationReason?: string | null;
}

interface Analytics {
  total: number;
  confirmed: number;
  checkedIn: number;
  checkedOut: number;
  cancelled: number;
  arrivalsToday: number;
  departuresToday: number;
  totalRevenue: number;
  monthRevenue: number;
  avgNights: number;
  avgTotal: number;
  rev30: { date: string; revenue: number }[];
  statusCounts: Record<string, number>;
  monthly: { key: string; bookings: number; revenue: number }[];
  topRooms: { roomId: string; bookings: number; revenue: number }[];
}

export const BOOKING_SOURCES = [
  { value: "direct",      label: "Direct / Walk-in" },
  { value: "phone",       label: "Phone" },
  { value: "whatsapp",    label: "WhatsApp" },
  { value: "booking.com", label: "Booking.com" },
  { value: "airbnb",      label: "Airbnb" },
  { value: "instagram",   label: "Instagram" },
  { value: "other",       label: "Other" },
];

export const BOOKING_TYPES = [
  { value: "Standard",  label: "Standard",  desc: "Regular booking — payment required" },
  { value: "Booking",   label: "Booking",   desc: "Already fully paid in booking" },
  { value: "HouseUse",  label: "House Use", desc: "Internal / complimentary — no charge" },
  { value: "Special",   label: "Special",   desc: "Special arrangement — no charge" },
] as const;

export type BookingType = "Standard" | "Booking" | "HouseUse" | "Special";

export type BookingForm = {
  guestName: string;
  phone: string;
  email: string;
  isVip: boolean;
  roomId: string;       // primary room (first selected) — backward compat
  roomIds: string[];    // all selected rooms (multi-room support)
  checkIn: string;
  checkOut: string;
  nights: number;
  total: number;
  status: string;
  guestCount: number;
  childrenCount: number;
  dayUse: boolean;
  season: string;
  source: string;
  notes: string;
  earlyCheckin: boolean;
  lateCheckout: boolean;
  bookingType: BookingType;
};

export const emptyBookingForm: BookingForm = {
  guestName: "",
  phone: "",
  email: "",
  isVip: false,
  roomId: "",
  roomIds: [],
  checkIn: "",
  checkOut: "",
  nights: 1,
  total: 0,
  status: "confirmed",
  guestCount: 1,
  childrenCount: 0,
  dayUse: false,
  season: "",
  source: "direct",
  notes: "",
  earlyCheckin: false,
  lateCheckout: false,
  bookingType: "Standard",
};

// ── Helpers ──────────────────────────────────────────────────
const STATUS_COLOR: Record<string, { color: string; bg: string }> = {
  confirmed:     { color: "#10b981", bg: "rgba(16,185,129,0.12)" },
  "checked-in":  { color: "#C9A84C", bg: "rgba(201,168,76,0.12)" },
  "in house":    { color: "#C9A84C", bg: "rgba(201,168,76,0.12)" },
  "checked-out": { color: "rgba(242,235,217,0.4)", bg: "rgba(100,116,139,0.12)" },
  cancelled:     { color: "#ef4444", bg: "rgba(239,68,68,0.12)" },
  pending:       { color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
  "on-hold":     { color: "#f97316", bg: "rgba(249,115,22,0.12)" },
};

function statusStyle(status: string) {
  const s = STATUS_COLOR[status?.toLowerCase()] || { color: "rgba(242,235,217,0.3)", bg: "rgba(148,163,184,0.1)" };
  return { color: s.color, borderColor: s.color, background: s.bg };
}

function fmt(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function exportBookingsCSV(bookings: Booking[]) {
  const headers = ["BookingID","Guest","Phone","Room","CheckIn","CheckOut","Nights","Total","Status"];
  const rows = bookings.map(b => [
    b.bookingId, b.guestName, b.phone, b.roomId,
    b.checkIn, b.checkOut, b.nights, b.total, b.status,
  ]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${v ?? ""}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "bookings.csv";
  a.click();
  URL.revokeObjectURL(url);
}

const generateBookingId = (roomId: string, roomIds?: string[]) => {
  const primary = (roomIds && roomIds.length > 0 ? roomIds[0] : roomId) || "BK";
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  const rand = Math.floor(Math.random() * 900) + 100;
  return `${primary}-${date}-${rand}`;
};

function computeNights(checkIn: string, checkOut: string): number {
  if (!checkIn || !checkOut) return 1;
  const diff = new Date(checkOut).getTime() - new Date(checkIn).getTime();
  const n = Math.ceil(diff / 86400000);
  return n > 0 ? n : 1;
}

const CHART_TOOLTIP = {
  contentStyle: { background: "#0E1E38", border: "1px solid rgba(201,168,76,0.15)", borderRadius: 10, fontSize: 12, color: "#F2EBD9" },
  labelStyle: { color: "rgba(242,235,217,0.3)" },
};
const TICK = { fontSize: 11, fill: "#475569" };
const GRID = { strokeDasharray: "3 3", stroke: "rgba(201,168,76,0.07)" };
const PIE_COLORS = ["#1a3a5c","#10b981","#64748b","#ef4444","#f59e0b","#06b6d4"];

// ── Shared field style ────────────────────────────────────────
const FS: React.CSSProperties = {
  background: "#0E1E38",
  border: "1px solid rgba(201,168,76,0.15)",
  borderRadius: 10,
  padding: "10px 14px",
  color: "#F2EBD9",
  fontSize: 14,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};
const LABEL: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, textTransform: "uppercase",
  letterSpacing: "0.8px", color: "rgba(242,235,217,0.45)",
};

// ── Add / Edit Booking Form Modal ────────────────────────────
export interface GuestOption {
  id?: number;
  name: string;
  phoneNumber: string;
  email?: string;
}

export interface RoomOption {
  roomId: string;
  roomNumber: string;
  roomType: string;
  status: string;
}

export interface BookingFormModalProps {
  mode: "add" | "edit";
  initial: BookingForm & { bookingId?: string };
  guests: GuestOption[];
  rooms: RoomOption[];
  seasons: { id: number; season: string; start_date: string; end_date: string }[];
  allBookings: Booking[];
  onClose: () => void;
  onSaved: (booking: Booking) => void;
  authHeaders: Record<string, string>;
}

export function BookingFormModal({ mode, initial, guests, rooms, seasons, allBookings, onClose, onSaved, authHeaders }: BookingFormModalProps) {
  const [form, setForm] = useState<BookingForm>({ ...initial });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [autoCalc, setAutoCalc] = useState(false);
  const [manualTotal, setManualTotal] = useState(false); // user overrode auto-calc
  const [roomAvailError, setRoomAvailError] = useState(""); // availability error under room field
  const [checkingAvail, setCheckingAvail] = useState(false);
  const [roomTotalsMap, setRoomTotalsMap] = useState<Record<string, number>>({}); // per-room auto-calc rates
  const [childrenAges, setChildrenAges] = useState<string[]>([]); // per-child ages for the children policy
  const [childrenInfo, setChildrenInfo] = useState<{ age: number; charge: number; status: string }[]>([]);

  // Keep the ages array length in sync with the children count
  useEffect(() => {
    setChildrenAges(prev => {
      const n = form.childrenCount || 0;
      const next = prev.slice(0, n);
      while (next.length < n) next.push("");
      return next;
    });
  }, [form.childrenCount]);

  // Multi-room: track selected rooms as array
  const [selectedRoomIds, setSelectedRoomIds] = useState<string[]>(() => {
    if (initial.roomIds && initial.roomIds.length > 0) return initial.roomIds;
    if (initial.roomId) return [initial.roomId];
    return [];
  });

  // Keep form.roomId and form.roomIds in sync with selectedRoomIds
  useEffect(() => {
    setForm(f => ({
      ...f,
      roomId:  selectedRoomIds[0] || "",
      roomIds: selectedRoomIds,
    }));
    setManualTotal(false);
    setAutoCalc(false);
  }, [selectedRoomIds]);

  // Auto-calculate total from room rates — sum across all selected rooms
  useEffect(() => {
    if (mode !== "add") return;
    if (selectedRoomIds.length === 0 || !form.checkIn || !form.checkOut) return;
    if (manualTotal) return;
    const isNoCharge = form.bookingType === "HouseUse" || form.bookingType === "Special";
    if (isNoCharge) return;
    const ages = childrenAges.filter(a => a !== "").join(",");
    Promise.all(
      selectedRoomIds.map((roomId, i) =>
        axios.get(`${API_BASE}/api/roomrates/calc`, {
          // Children apply to the first room (the family's room)
          params: { roomId, checkin: form.checkIn, checkout: form.checkOut, ...(i === 0 && ages ? { children: ages } : {}) },
          headers: authHeaders,
        }).then(r => {
          if (i === 0) setChildrenInfo(r.data.children || []);
          return r.data.total || 0;
        }).catch(() => 0)
      )
    ).then(totals => {
      const map: Record<string, number> = {};
      selectedRoomIds.forEach((rid, i) => { map[rid] = totals[i] || 0; });
      const sum = totals.reduce((a, b) => a + b, 0);
      if (sum > 0) { setForm(f => ({ ...f, total: sum })); setAutoCalc(true); setRoomTotalsMap(map); }
    });
  }, [selectedRoomIds.join(","), form.checkIn, form.checkOut, manualTotal, childrenAges.join(",")]);

  // Availability check for the most-recently-added room
  useEffect(() => {
    const roomId = selectedRoomIds[selectedRoomIds.length - 1];
    if (!roomId || !form.checkIn || !form.checkOut) { setRoomAvailError(""); return; }
    setCheckingAvail(true);
    setRoomAvailError("");
    axios.get(`${API_BASE}/api/rooms/check-available`, {
      params: {
        roomId,
        checkin: form.checkIn,
        checkout: form.checkOut,
        excludeBookingId: mode === "edit" ? initial.bookingId : undefined,
      },
      headers: authHeaders,
    }).then(res => {
      if (!res.data.available) {
        const cb = res.data.conflictingBookings?.[0];
        if (cb) {
          setRoomAvailError(`Room is already booked from ${cb.checkin} to ${cb.checkout}`);
        } else if (res.data.blockedDates?.length) {
          setRoomAvailError(`Room is blocked on: ${res.data.blockedDates.slice(0,3).join(", ")}${res.data.blockedDates.length > 3 ? "…" : ""}`);
        } else {
          setRoomAvailError("Room is not available for the selected dates");
        }
      } else {
        setRoomAvailError("");
      }
    }).catch(() => {}).finally(() => setCheckingAvail(false));
  }, [selectedRoomIds.join(","), form.checkIn, form.checkOut]);

  // Guest selector — pre-match existing guest when editing
  const [selectedGuest, setSelectedGuest] = useState<GuestOption | null>(() => {
    if (mode === "edit" && initial.phone) {
      const norm = (p: string) => String(p).replace(/\D/g, "");
      return guests.find(g => norm(g.phoneNumber) === norm(initial.phone)) ?? null;
    }
    return null;
  });
  const [guestSearch, setGuestSearch] = useState(initial.guestName || "");
  const [nameDropOpen, setNameDropOpen] = useState(false);
  const [phoneDropOpen, setPhoneDropOpen] = useState(false);
  const guestMode = selectedGuest ? "existing" : "new";

  // Room search input — selected rooms are shown as chips, this is just the filter input
  const [roomSearch, setRoomSearch] = useState("");
  const [roomDropOpen, setRoomDropOpen] = useState(false);


  const guestsByName = form.guestName.trim()
    ? guests.filter(g => g.name.toLowerCase().includes(form.guestName.toLowerCase()))
    : guests;

  const normalizePhone = (p: string) => String(p).replace(/\D/g, "");
  const guestsByPhone = form.phone.trim()
    ? guests.filter(g => normalizePhone(String(g.phoneNumber)).includes(normalizePhone(form.phone)))
    : guests;

  // Available rooms: not out-of-service AND no overlapping booking for selected dates
  const availableRooms = useMemo(() => {
    const ci = form.checkIn;
    const co = form.checkOut;
    return rooms.filter(r => {
      if (r.status?.toLowerCase() === "out of service") return false;
      if (!ci || !co) return true; // no dates yet → show all non-OOS rooms
      const hasConflict = allBookings.some(b => {
        if (b.roomId !== r.roomId) return false;
        if (b.status?.toLowerCase() === "cancelled") return false;
        if (mode === "edit" && b.bookingId === initial.bookingId) return false;
        if (!b.checkIn || !b.checkOut) return false;
        const bci = String(b.checkIn).slice(0, 10);
        const bco = String(b.checkOut).slice(0, 10);
        return bci < co && bco > ci; // overlap
      });
      return !hasConflict;
    });
  }, [rooms, allBookings, form.checkIn, form.checkOut, mode, initial.bookingId]);

  const filteredRooms = availableRooms.filter(r => {
    if (selectedRoomIds.includes(r.roomId)) return false; // already selected
    if (!roomSearch) return true;
    return (
      r.roomNumber.toLowerCase().includes(roomSearch.toLowerCase()) ||
      r.roomId.toLowerCase().includes(roomSearch.toLowerCase()) ||
      r.roomType.toLowerCase().includes(roomSearch.toLowerCase())
    );
  });

  // All rooms with availability status for the left panel
  const roomsWithStatus = useMemo(() => {
    const ci = form.checkIn;
    const co = form.checkOut;
    return rooms
      .filter(r => !roomSearch || r.roomNumber.toLowerCase().includes(roomSearch.toLowerCase()) || r.roomType.toLowerCase().includes(roomSearch.toLowerCase()))
      .map(r => {
        const selected = selectedRoomIds.includes(r.roomId);
        if (r.status?.toLowerCase() === "out of service") return { ...r, avail: "oos" as const, conflictInfo: "" };
        if (!ci || !co) return { ...r, avail: selected ? "selected" as const : "unknown" as const, conflictInfo: "" };
        const conflictBk = allBookings.find(b => {
          if (b.roomId !== r.roomId) return false;
          if (b.status?.toLowerCase() === "cancelled" || b.status?.toLowerCase() === "deleted") return false;
          if (mode === "edit" && b.bookingId === initial.bookingId) return false;
          if (!b.checkIn || !b.checkOut) return false;
          const bci = String(b.checkIn).slice(0, 10);
          const bco = String(b.checkOut).slice(0, 10);
          return bci < co && bco > ci;
        });
        if (selected) return { ...r, avail: "selected" as const, conflictInfo: "" };
        if (conflictBk) return { ...r, avail: "booked" as const, conflictInfo: `${conflictBk.checkIn} – ${conflictBk.checkOut}` };
        return { ...r, avail: "free" as const, conflictInfo: "" };
      })
      .sort((a, b) => {
        const order = { selected: 0, free: 1, unknown: 2, booked: 3, oos: 4 };
        return (order[a.avail] ?? 5) - (order[b.avail] ?? 5) || a.roomNumber.localeCompare(b.roomNumber, undefined, { numeric: true });
      });
  }, [rooms, allBookings, selectedRoomIds, form.checkIn, form.checkOut, roomSearch, mode, initial.bookingId]);

  const pickRoom = (r: RoomOption) => {
    setSelectedRoomIds(prev => prev.includes(r.roomId) ? prev : [...prev, r.roomId]);
    setRoomSearch("");
    setRoomDropOpen(false);
  };

  const removeRoom = (roomId: string) => {
    setSelectedRoomIds(prev => prev.filter(id => id !== roomId));
  };

  const pickGuest = (g: GuestOption) => {
    setSelectedGuest(g);
    setGuestSearch(g.name);
    setNameDropOpen(false);
    setPhoneDropOpen(false);
    setForm(prev => ({ ...prev, guestName: g.name, phone: g.phoneNumber }));
  };

  const addDays = (dateStr: string, n: number) => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
  };

  const set = <K extends keyof BookingForm>(field: K, value: BookingForm[K]) => {
    // Changing dates resets the manual total override so auto-calc triggers again
    if (field === "checkIn" || field === "checkOut") {
      setManualTotal(false);
      setAutoCalc(false);
    }
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === "checkIn") {
        const ci = String(value);
        if (prev.checkOut) {
          next.nights = computeNights(ci, prev.checkOut);
        } else if (prev.nights > 0) {
          next.checkOut = addDays(ci, Number(prev.nights));
        }
      } else if (field === "checkOut") {
        const co = String(value);
        if (prev.checkIn && co) next.nights = computeNights(prev.checkIn, co);
      } else if (field === "nights") {
        const n = Number(value);
        if (prev.checkIn && n > 0) next.checkOut = addDays(prev.checkIn, n);
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.guestName.trim() || !form.checkIn || !form.checkOut) {
      setError("Guest name, check-in, and check-out are required.");
      return;
    }
    if (selectedRoomIds.length === 0) {
      setError("At least one room is required. Please select a room before saving.");
      return;
    }
    if (roomAvailError) {
      setError(roomAvailError);
      return;
    }
    setSaving(true);
    setError("");
    try {
      // If new guest in add mode → create guest first with source="booking"
      if (mode === "add" && guestMode === "new" && !selectedGuest) {
        const gPhone = form.phone.replace(/\D/g, "");
        await axios.post(`${API_BASE}/api/guests`, {
          name: form.guestName,
          phoneNumber: gPhone ? Number(gPhone) : null,
          email: form.email || null,
          isVIP: form.isVip,
          loyaltyPoints: 0,
          source: "booking",
        }, { headers: authHeaders });
      }

      const bookingId = mode === "add" ? generateBookingId(form.roomId, selectedRoomIds) : initial.bookingId!;
      const phone = form.phone.replace(/\D/g, "");
      const isNoCharge = form.bookingType === "HouseUse" || form.bookingType === "Special";
      // Send the exact per-room rates only when they came from auto-calc and weren't
      // manually overridden — otherwise let the backend split the total evenly.
      const sendRoomTotals = !isNoCharge && autoCalc && !manualTotal
        && selectedRoomIds.length > 0
        && selectedRoomIds.every(rid => roomTotalsMap[rid] != null);
      const body = {
        bookingid: bookingId,
        roomid: selectedRoomIds[0] || form.roomId,
        roomids: selectedRoomIds,
        ...(sendRoomTotals ? { roomTotals: roomTotalsMap } : {}),
        guestname: form.guestName,
        phonenumber: phone ? Number(phone) : null,
        checkin: form.checkIn,
        checkout: form.checkOut,
        nights: form.nights,
        total: isNoCharge ? 0 : form.total,
        status: form.status,
        guest_count: form.guestCount,
        children_count: form.childrenCount || 0,
        children_ages: childrenAges.filter(a => a !== "").map(Number),
        day_use: form.dayUse || false,
        season: form.season,
        createdat: mode === "add" ? new Date().toISOString() : undefined,
        source: form.source || "direct",
        notes: form.notes || null,
        early_checkin: form.earlyCheckin || false,
        late_checkout: form.lateCheckout || false,
        booking_type: form.bookingType || "Standard",
      };

      let res;
      if (mode === "add") {
        res = await axios.post(`${API_BASE}/api/bookings`, body, { headers: authHeaders });
      } else {
        res = await axios.put(`${API_BASE}/api/bookings/${initial.bookingId}`, body, { headers: authHeaders });
      }

      const raw = res.data.booking ?? res.data;
      const saved: Booking = {
        id: raw.bookingid ?? raw.id ?? bookingId,
        bookingId: raw.bookingid ?? bookingId,
        roomId: raw.roomid ?? raw.roomId ?? form.roomId,
        guestName: raw.guestname ?? raw.guestName ?? form.guestName,
        phone: String(raw.phonenumber ?? raw.phone ?? form.phone ?? ""),
        checkIn: raw.checkin ?? raw.checkIn ?? form.checkIn,
        checkOut: raw.checkout ?? raw.checkOut ?? form.checkOut,
        nights: raw.nights ?? form.nights,
        total: Number(raw.total ?? form.total),
        status: raw.status ?? form.status,
        guestCount: raw.guest_count ?? raw.guestCount ?? form.guestCount,
        season: raw.season ?? form.season ?? null,
        createdAt: raw.createdat ?? raw.createdAt ?? new Date().toISOString(),
      };
      onSaved(saved);
    } catch (err: any) {
      console.error("Booking save error:", err?.response?.data ?? err?.message);
      setError(err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message ?? "An error occurred. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bp-modal-overlay" onClick={onClose}>
      <div className="bp-modal" style={{ maxWidth: 980, padding: 0, display: "flex", flexDirection: "column", overflow: "hidden" }} onClick={e => e.stopPropagation()}>

        {/* Modal header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 22px 14px", borderBottom: "1px solid rgba(201,168,76,0.08)", flexShrink: 0 }}>
          <h2 className="bp-modal-title" style={{ margin: 0 }}>{mode === "add" ? "Add New Booking" : "Edit Booking"}</h2>
          <button className="bp-modal-close" style={{ position: "static" }} onClick={onClose}><FontAwesomeIcon icon={faXmark} /></button>
        </div>

        {error && (
          <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)", padding: "10px 22px", color: "#f87171", fontSize: 13, flexShrink: 0 }}>
            {error}
          </div>
        )}

        {/* Split body */}
        <div style={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0 }}>

          {/* ── LEFT: Rooms Panel ── */}
          <div style={{ width: 260, borderRight: "1px solid rgba(201,168,76,0.08)", display: "flex", flexDirection: "column", flexShrink: 0 }}>
            <div style={{ padding: "12px 14px 8px", borderBottom: "1px solid rgba(201,168,76,0.08)" }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.7px", color: "rgba(242,235,217,0.3)", marginBottom: 8 }}>
                Rooms {form.checkIn && form.checkOut ? `· ${selectedRoomIds.length} selected` : "· select dates first"}
              </div>
              <input
                type="text"
                placeholder="Search rooms…"
                value={roomSearch}
                onChange={e => setRoomSearch(e.target.value)}
                style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: "1px solid rgba(201,168,76,0.15)", fontSize: 12, color: "#F2EBD9", outline: "none", boxSizing: "border-box" }}
              />
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "8px 10px" }}>
              {roomsWithStatus.map(r => {
                const isSelected = r.avail === "selected";
                const isBooked   = r.avail === "booked";
                const isOos      = r.avail === "oos";
                const isFree     = r.avail === "free" || r.avail === "unknown";
                return (
                  <div
                    key={r.roomId}
                    onClick={() => !isOos && !isBooked && (isSelected ? removeRoom(r.roomId) : pickRoom(r))}
                    style={{
                      padding: "9px 10px", borderRadius: 10, marginBottom: 4, cursor: isOos || isBooked ? "default" : "pointer",
                      border: `1.5px solid ${isSelected ? "rgba(16,185,129,0.4)" : isBooked ? "#fecaca" : "rgba(201,168,76,0.07)"}`,
                      background: isSelected ? "rgba(16,185,129,0.07)" : isBooked ? "rgba(239,68,68,0.08)" : isOos ? "rgba(255,255,255,0.03)" : "#0E1E38",
                      opacity: isOos ? 0.45 : 1,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: isBooked ? "#f87171" : isSelected ? "#34d399" : "#F2EBD9" }}>
                        #{r.roomNumber}
                      </span>
                      {isSelected && <span style={{ fontSize: 11, color: "#10b981", fontWeight: 700 }}>✓</span>}
                      {isBooked  && <span style={{ fontSize: 10, color: "#f87171", fontWeight: 700 }}>Booked</span>}
                      {isOos     && <span style={{ fontSize: 10, color: "rgba(242,235,217,0.3)", fontWeight: 700 }}>OOS</span>}
                    </div>
                    <div style={{ fontSize: 11, color: "rgba(242,235,217,0.6)", marginTop: 2 }}>{r.roomType}</div>
                    {isBooked && r.conflictInfo && (
                      <div style={{ fontSize: 10, color: "#f87171", marginTop: 2 }}>{r.conflictInfo}</div>
                    )}
                  </div>
                );
              })}
              {roomsWithStatus.length === 0 && (
                <div style={{ fontSize: 12, color: "rgba(242,235,217,0.3)", padding: "16px 8px", textAlign: "center" }}>No rooms found</div>
              )}
            </div>
          </div>

          {/* ── RIGHT: Form ── */}
          <div style={{ flex: 1, overflowY: "auto", padding: "20px 22px" }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>

            {/* ── GUEST — unified autocomplete field ── */}
            <div style={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <label style={LABEL}>Guest Name *</label>
                {selectedGuest && (
                  <span style={{ fontSize: 11, color: "#34d399", fontWeight: 600 }}>
                    <FontAwesomeIcon icon={faCheck} style={{ marginRight: 4 }} />
                    Existing guest
                  </span>
                )}
                {!selectedGuest && (form.guestName.trim() || form.phone.trim()) && (
                  <span style={{ fontSize: 11, color: "#f59e0b", fontWeight: 600 }}>New guest</span>
                )}
              </div>
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  placeholder="Type name to search existing guests…"
                  value={form.guestName}
                  onChange={e => {
                    const val = e.target.value;
                    setNameDropOpen(true);
                    setSelectedGuest(null);
                    setForm(f => ({ ...f, guestName: val }));
                  }}
                  onFocus={() => setNameDropOpen(true)}
                  onBlur={() => setTimeout(() => setNameDropOpen(false), 150)}
                  required
                  style={{
                    ...FS,
                    borderColor: selectedGuest
                      ? "rgba(52,211,153,0.35)"
                      : form.guestName.trim() ? "rgba(245,158,11,0.3)" : "rgba(201,168,76,0.12)",
                  }}
                />
                {nameDropOpen && guestsByName.length > 0 && (
                  <div style={{
                    position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 200,
                    background: "#0E1E38", border: "1px solid rgba(201,168,76,0.15)", borderRadius: 10,
                    maxHeight: 220, overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
                  }}>
                    {guestsByName.slice(0, 30).map(g => (
                      <div
                        key={g.id ?? g.phoneNumber}
                        onMouseDown={() => pickGuest(g)}
                        style={{ padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                        onMouseEnter={e => (e.currentTarget.style.background = "rgba(201,168,76,0.12)")}
                        onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 13, color: "#F2EBD9", fontWeight: 600 }}>{g.name}</span>
                          <span style={{ fontSize: 12, color: "#C9A84C", fontFamily: "monospace" }}>{g.phoneNumber}</span>
                        </div>
                        {g.email && <div style={{ fontSize: 11, color: "rgba(242,235,217,0.45)", marginTop: 2 }}>{g.email}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Phone — own dropdown below it, filters by phone number */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6, position: "relative" }}>
              <label style={LABEL}>Phone</label>
              <input
                type="tel"
                placeholder="+20 10..."
                value={form.phone}
                onChange={e => {
                  const val = e.target.value;
                  set("phone", val);
                  setSelectedGuest(null);
                  setPhoneDropOpen(true);
                }}
                onFocus={() => setPhoneDropOpen(true)}
                onBlur={() => setTimeout(() => setPhoneDropOpen(false), 150)}
                style={{ ...FS }}
              />
              {phoneDropOpen && guestsByPhone.length > 0 && (
                <div style={{
                  position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 200,
                  background: "#0E1E38", border: "1px solid rgba(201,168,76,0.15)", borderRadius: 10,
                  maxHeight: 220, overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
                }}>
                  {guestsByPhone.slice(0, 30).map(g => (
                    <div
                      key={g.id ?? g.phoneNumber}
                      onMouseDown={() => pickGuest(g)}
                      style={{ padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                      onMouseEnter={e => (e.currentTarget.style.background = "rgba(201,168,76,0.12)")}
                      onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 12, color: "#C9A84C", fontFamily: "monospace" }}>{g.phoneNumber}</span>
                        <span style={{ fontSize: 13, color: "#F2EBD9", fontWeight: 600 }}>{g.name}</span>
                      </div>
                      {g.email && <div style={{ fontSize: 11, color: "rgba(242,235,217,0.45)", marginTop: 2 }}>{g.email}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Email + VIP — only for new guests */}
            {!selectedGuest && (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={LABEL}>Email <span style={{ color: "rgba(242,235,217,0.45)", fontWeight: 400, textTransform: "none" }}>(optional)</span></label>
                  <input
                    type="email"
                    placeholder="guest@email.com"
                    value={form.email}
                    onChange={e => set("email", e.target.value)}
                    style={FS}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, justifyContent: "flex-end" }}>
                  <label style={{ ...LABEL, display: "flex", alignItems: "center", gap: 10, cursor: "pointer", paddingBottom: 10 }}>
                    <input
                      type="checkbox"
                      checked={form.isVip}
                      onChange={e => set("isVip", e.target.checked)}
                      style={{ width: 16, height: 16, accentColor: "#f59e0b" }}
                    />
                    <span>VIP Guest</span>
                    <span style={{ fontSize: 14 }}>⭐</span>
                  </label>
                </div>
              </>
            )}

            {/* Selected rooms summary (read-only chips, selections made via left panel) */}
            {selectedRoomIds.length > 0 && (
              <div style={{ gridColumn: "1 / -1", display: "flex", flexWrap: "wrap", gap: 6 }}>
                {selectedRoomIds.map(rid => {
                  const r = rooms.find(x => x.roomId === rid);
                  return (
                    <span key={rid} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.3)", borderRadius: 20, padding: "4px 10px", fontSize: 12, color: "#10b981" }}>
                      <span style={{ fontWeight: 700 }}>Room {r?.roomNumber || rid}</span>
                      {r && <span style={{ color: "rgba(242,235,217,0.4)", fontSize: 11 }}>{r.roomType}</span>}
                      <button type="button" onClick={() => removeRoom(rid)} style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(242,235,217,0.3)", padding: 0, fontSize: 12 }}>
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            {selectedRoomIds.length === 0 && (
              <div style={{ gridColumn: "1 / -1", fontSize: 12, color: "rgba(242,235,217,0.3)", padding: "6px 0" }}>
                Select a room from the panel on the left
              </div>
            )}
            {roomAvailError && selectedRoomIds.length > 0 && (
              <div style={{ gridColumn: "1 / -1", fontSize: 11, color: "#f87171", fontWeight: 600, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 7, padding: "6px 10px" }}>
                <FontAwesomeIcon icon={faTriangleExclamation} /> {roomAvailError}
              </div>
            )}

            {/* Check In */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={LABEL}>Check In *</label>
              <input type="date" value={form.checkIn} onChange={e => set("checkIn", e.target.value)} required style={FS} />
            </div>

            {/* Check Out */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={LABEL}>Check Out *</label>
              <input type="date" value={form.checkOut} onChange={e => set("checkOut", e.target.value)} required style={FS} />
            </div>

            {/* Nights — editable, or auto-calculated from dates */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ ...LABEL, display: "flex", justifyContent: "space-between" }}>
                <span>Nights</span>
                <span style={{ fontSize: 10, color: "rgba(242,235,217,0.3)", textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>sets checkout automatically</span>
              </label>
              <input
                type="number"
                min={1}
                value={form.nights}
                onChange={e => set("nights", parseInt(e.target.value) || 1)}
                style={FS}
              />
            </div>

            {/* Total */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ ...LABEL, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>Total (EGP)</span>
                {(form.bookingType === "HouseUse" || form.bookingType === "Special") && (
                  <span style={{ fontSize: 9, color: "#34d399", fontWeight: 600, textTransform: "none", letterSpacing: 0 }}>No charge</span>
                )}
                {form.bookingType === "Booking" && (
                  <span style={{ fontSize: 9, color: "#C9A84C", fontWeight: 600, textTransform: "none", letterSpacing: 0 }}>Pre-paid</span>
                )}
                {autoCalc && form.bookingType === "Standard" && (
                  <span style={{ fontSize: 9, color: "#34d399", fontWeight: 600, textTransform: "none", letterSpacing: 0 }}>Auto-calculated</span>
                )}
              </label>
              <input
                type="number" min={0} placeholder="0"
                value={form.total}
                disabled={form.bookingType === "HouseUse" || form.bookingType === "Special"}
                onChange={e => { set("total", parseFloat(e.target.value) || 0); setAutoCalc(false); setManualTotal(true); }}
                style={{
                  ...FS,
                  opacity: (form.bookingType === "HouseUse" || form.bookingType === "Special") ? 0.5 : 1,
                  cursor: (form.bookingType === "HouseUse" || form.bookingType === "Special") ? "not-allowed" : undefined,
                }}
              />
            </div>

            {/* Booking Type */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={LABEL}>Booking Type</label>
              <select
                value={form.bookingType}
                onChange={e => {
                  const t = e.target.value as BookingType;
                  set("bookingType", t);
                  if (t === "HouseUse" || t === "Special") set("total", 0 as any);
                }}
                style={{
                  ...FS,
                  borderColor: form.bookingType === "HouseUse" || form.bookingType === "Special"
                    ? "rgba(52,211,153,0.35)"
                    : form.bookingType === "Booking"
                    ? "rgba(14,34,64,0.10)"
                    : "rgba(201,168,76,0.12)",
                }}
              >
                {BOOKING_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label} — {t.desc}</option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={LABEL}>Status</label>
              <select value={form.status} onChange={e => set("status", e.target.value)} style={FS}>
                <option value="confirmed">Confirmed</option>
                <option value="checked-in">In House (Checked-In)</option>
                <option value="checked-out">Checked-Out</option>
                <option value="on-hold">On Hold (WhatsApp — 2 days)</option>
                <option value="cancelled">Cancelled</option>
                <option value="pending">Pending Payment</option>
              </select>
            </div>

            {/* Guest Count + Children */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={LABEL}>Adults</label>
              <input type="number" min={1} value={form.guestCount} onChange={e => set("guestCount", parseInt(e.target.value) || 1)} style={FS} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={LABEL}>Children</label>
              <input type="number" min={0} value={form.childrenCount} onChange={e => set("childrenCount", parseInt(e.target.value) || 0)} style={FS} />
            </div>

            {/* Children ages — drives the children policy (free / half / full) automatically */}
            {form.childrenCount > 0 && (
              <div style={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={LABEL}>Children Ages <span style={{ color: "rgba(242,235,217,0.4)", fontWeight: 400, textTransform: "none" }}>(applies the children policy)</span></label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {childrenAges.map((age, i) => (
                    <input
                      key={i}
                      type="number" min={0} max={17}
                      placeholder={`Child ${i + 1}`}
                      value={age}
                      onChange={e => setChildrenAges(prev => prev.map((a, idx) => idx === i ? e.target.value : a))}
                      style={{ ...FS, width: 96 }}
                    />
                  ))}
                </div>
                {childrenInfo.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 2 }}>
                    {childrenInfo.map((c, i) => (
                      <span key={i} style={{ fontSize: 12, color: "rgba(242,235,217,0.55)" }}>
                        Child {i + 1} ({c.age}y):{" "}
                        <strong style={{ color: c.status === "free" ? "#34d399" : c.status === "half" ? "#fbbf24" : "#F2EBD9" }}>
                          {c.status === "free" ? "Free" : c.status === "half" ? `Half · EGP ${c.charge.toLocaleString()}` : `EGP ${c.charge.toLocaleString()}`}
                        </strong>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Season */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={LABEL}>Season</label>
              <select value={form.season} onChange={e => set("season", e.target.value)} style={FS}>
                <option value="">— Select season —</option>
                {seasons.map(s => (
                  <option key={s.id} value={s.season}>{s.season}</option>
                ))}
              </select>
            </div>

            {/* Source */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={LABEL}>Booking Source</label>
              <select value={form.source} onChange={e => set("source", e.target.value)} style={FS}>
                {BOOKING_SOURCES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            {/* Day Use + Early/Late checkboxes */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10, justifyContent: "flex-end", paddingBottom: 2 }}>
              <label style={{ ...LABEL, display: "flex", alignItems: "center", gap: 10, cursor: "pointer", textTransform: "none", letterSpacing: 0, fontSize: 13 }}>
                <input type="checkbox" checked={form.dayUse} onChange={e => {
                  const v = e.target.checked;
                  set("dayUse", v);
                  if (v) {
                    const td = new Date(); const y = td.getFullYear(), m = String(td.getMonth()+1).padStart(2,"0"), d = String(td.getDate()).padStart(2,"0");
                    const iso = `${y}-${m}-${d}`;
                    setForm(f => ({ ...f, dayUse: true, checkIn: iso, checkOut: iso, nights: 0 }));
                  }
                }} style={{ width: 15, height: 15, accentColor: "#f59e0b" }} />
                Day Use <span style={{ fontSize: 10, color: "#f59e0b", fontWeight: 700 }}>TODAY ONLY</span>
              </label>
              <label style={{ ...LABEL, display: "flex", alignItems: "center", gap: 10, cursor: "pointer", textTransform: "none", letterSpacing: 0, fontSize: 13 }}>
                <input type="checkbox" checked={form.earlyCheckin} onChange={e => set("earlyCheckin", e.target.checked)}
                  style={{ width: 15, height: 15, accentColor: "#1a3a5c" }} />
                Early check-in request
              </label>
              <label style={{ ...LABEL, display: "flex", alignItems: "center", gap: 10, cursor: "pointer", textTransform: "none", letterSpacing: 0, fontSize: 13 }}>
                <input type="checkbox" checked={form.lateCheckout} onChange={e => set("lateCheckout", e.target.checked)}
                  style={{ width: 15, height: 15, accentColor: "#1a3a5c" }} />
                Late check-out request
              </label>
            </div>

            {/* Notes / Special Requests */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6, gridColumn: "1 / -1" }}>
              <label style={LABEL}>Notes / Special Requests</label>
              <textarea
                placeholder="Allergies, room preferences, special occasions…"
                value={form.notes}
                onChange={e => set("notes", e.target.value)}
                rows={3}
                style={{ ...FS, resize: "vertical", fontFamily: "inherit" }}
              />
            </div>

          </div>

          <div className="bp-modal-footer">
            <button type="button" className="bp-btn secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="bp-btn primary" disabled={saving || !!roomAvailError || checkingAvail || selectedRoomIds.length === 0}>
              {saving ? "Saving…" : checkingAvail ? "Checking…" : mode === "add" ? "Add Booking" : "Save Changes"}
            </button>
          </div>
        </form>
          </div>{/* end right form */}
        </div>{/* end split body */}
      </div>
    </div>
  );
}

// ── Booking Detail Modal ─────────────────────────────────────
interface BookingModalProps {
  booking: Booking;
  onClose: () => void;
  onEdit: (booking: Booking) => void;
  onDelete: (bookingId: string) => void;
}

function BookingModal({ booking, onClose, onEdit, onDelete }: BookingModalProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState("");

  const token = localStorage.getItem("ai2_token");
  const authHeaders = { Authorization: `Bearer ${token}` };
  const st = statusStyle(booking.status);

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError("");
    try {
      await axios.delete(`${API_BASE}/api/bookings/${booking.bookingId}`, { headers: authHeaders });
      onDelete(booking.bookingId);
    } catch (err: any) {
      setDeleteError(err?.response?.data?.error ?? err?.message ?? "Delete failed.");
      setDeleting(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) { setCancelError("Please enter a cancellation reason."); return; }
    setCancelling(true); setCancelError("");
    try {
      await axios.put(`${API_BASE}/api/bookings/${booking.bookingId}/cancel`, { reason: cancelReason }, { headers: authHeaders });
      onDelete(booking.bookingId); // refresh list
    } catch (err: any) {
      setCancelError(err?.response?.data?.error ?? err?.message ?? "Cancellation failed.");
      setCancelling(false);
    }
  };

  const isCancelled = ["cancelled", "deleted"].includes(booking.status?.toLowerCase());

  return (
    <div className="bp-modal-overlay" onClick={onClose}>
      <div className="bp-modal" onClick={e => e.stopPropagation()}>
        <button className="bp-modal-close" onClick={onClose}>
          <FontAwesomeIcon icon={faXmark} />
        </button>

        <h2 className="bp-modal-title" style={{ paddingRight: 32 }}>
          Booking {booking.bookingId}
          {booking.dayUse && (
            <span style={{ fontSize: 11, background: "#fef3c7", color: "#92400e", borderRadius: 20, padding: "2px 9px", marginLeft: 10, fontWeight: 700 }}>Day Use</span>
          )}
        </h2>

        <div className="bp-modal-grid">
          <div className="bp-modal-item">
            <span className="bp-modal-label">Guest</span>
            <span className="bp-modal-value">{booking.guestName}</span>
          </div>
          <div className="bp-modal-item">
            <span className="bp-modal-label">Phone</span>
            <span className="bp-modal-value">{booking.phone || "—"}</span>
          </div>
          <div className="bp-modal-item">
            <span className="bp-modal-label">Room{(booking.allRoomIds?.length ?? 0) > 1 ? "s" : ""}</span>
            {booking.allRoomNumbers && booking.allRoomNumbers.length > 1 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 2 }}>
                {booking.allRoomNumbers.map((rn, i) => (
                  <span key={i} style={{ background: "rgba(201,168,76,0.07)", border: "1px solid rgba(201,168,76,0.15)", borderRadius: 7, padding: "2px 9px", fontSize: 13, fontWeight: 700, color: "#FDFAF4" }}>
                    {rn}
                    <span style={{ fontFamily: "monospace", fontSize: 10, color: "rgba(242,235,217,0.3)", marginLeft: 5 }}>{booking.allRoomIds?.[i]}</span>
                  </span>
                ))}
              </div>
            ) : (
              <span className="bp-modal-value">{booking.roomId || "—"}</span>
            )}
          </div>
          <div className="bp-modal-item">
            <span className="bp-modal-label">Status</span>
            <span className="bp-modal-value" style={{ color: st.color }}>{booking.status}</span>
          </div>
          <div className="bp-modal-item">
            <span className="bp-modal-label">Check In</span>
            <span className="bp-modal-value">{fmt(booking.checkIn)}</span>
          </div>
          <div className="bp-modal-item">
            <span className="bp-modal-label">Check Out</span>
            <span className="bp-modal-value">{fmt(booking.checkOut)}</span>
          </div>
          <div className="bp-modal-item">
            <span className="bp-modal-label">Nights</span>
            <span className="bp-modal-value">{booking.dayUse ? "Day Use" : booking.nights}</span>
          </div>
          <div className="bp-modal-item">
            <span className="bp-modal-label">Adults / Children</span>
            <span className="bp-modal-value">{booking.guestCount} / {booking.childrenCount ?? 0}</span>
          </div>
          {(() => {
            const bk = breakdownFromTotal(booking.total || 0, booking.nights);
            return (
              <div className="bp-modal-item" style={{ gridColumn: "1 / -1" }}>
                <span className="bp-modal-label">Price breakdown</span>
                <div className="bp-breakdown">
                  <div className="bp-bk-row"><span>Subtotal</span><span>EGP {bk.subtotal.toLocaleString()}</span></div>
                  {bk.tax > 0 && <div className="bp-bk-row"><span>Tax ({(bk.taxRate * 100).toFixed(0)}%)</span><span>EGP {bk.tax.toLocaleString()}</span></div>}
                  {bk.service > 0 && <div className="bp-bk-row"><span>Service ({(bk.serviceRate * 100).toFixed(0)}%)</span><span>EGP {bk.service.toLocaleString()}</span></div>}
                  <div className="bp-bk-row bp-bk-total"><span>Total</span><span>EGP {bk.total.toLocaleString()}</span></div>
                  <div className="bp-bk-row bp-bk-deposit"><span>Deposit (1 night)</span><span>EGP {bk.deposit.toLocaleString()}</span></div>
                </div>
              </div>
            );
          })()}
          <div className="bp-modal-item">
            <span className="bp-modal-label">Season</span>
            <span className="bp-modal-value">{booking.season || "—"}</span>
          </div>
          {booking.bookingType && (
            <div className="bp-modal-item">
              <span className="bp-modal-label">Booking Type</span>
              <span className="bp-modal-value">{booking.bookingType}</span>
            </div>
          )}
          {booking.source && (
            <div className="bp-modal-item">
              <span className="bp-modal-label">Source</span>
              <span className="bp-modal-value" style={{ textTransform: "capitalize" }}>{booking.source}</span>
            </div>
          )}
          <div className="bp-modal-item" style={{ gridColumn: "1 / -1" }}>
            <span className="bp-modal-label">Created At</span>
            <span className="bp-modal-value">{fmt(booking.createdAt)}</span>
          </div>
          {booking.notes && (
            <div className="bp-modal-item" style={{ gridColumn: "1 / -1" }}>
              <span className="bp-modal-label">Notes</span>
              <span className="bp-modal-value" style={{ whiteSpace: "pre-wrap" }}>{booking.notes}</span>
            </div>
          )}
          {booking.cancellationReason && (
            <div className="bp-modal-item" style={{ gridColumn: "1 / -1" }}>
              <span className="bp-modal-label" style={{ color: "#ef4444" }}>Cancellation Reason</span>
              <span className="bp-modal-value" style={{ color: "#f87171" }}>{booking.cancellationReason}</span>
            </div>
          )}
        </div>

        {/* Cancel confirm */}
        {cancelOpen && (
          <div style={{ background: "rgba(245,158,11,0.07)", border: "1px solid rgba(245,158,11,0.25)", borderRadius: 12, padding: "12px 16px", marginBottom: 12 }}>
            <p style={{ margin: "0 0 8px", fontSize: 13, color: "#92400e", fontWeight: 600 }}>
              Cancel booking {booking.bookingId}
            </p>
            <textarea
              placeholder="Reason for cancellation…"
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              rows={2}
              style={{ width: "100%", borderRadius: 8, border: "1px solid #fcd34d", padding: "8px 10px", fontSize: 13, resize: "none", fontFamily: "inherit", boxSizing: "border-box", outline: "none", marginBottom: 8 }}
            />
            {cancelError && <p style={{ margin: "0 0 6px", fontSize: 12, color: "#ef4444" }}>{cancelError}</p>}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="bp-btn primary"
                style={{ background: "#f59e0b", boxShadow: "none" }}
                onClick={handleCancel}
                disabled={cancelling}
              >
                {cancelling ? "Cancelling…" : "Confirm Cancel"}
              </button>
              <button className="bp-btn secondary" onClick={() => { setCancelOpen(false); setCancelReason(""); setCancelError(""); }}>Back</button>
            </div>
          </div>
        )}

        {/* Delete confirm */}
        {confirmDelete && (
          <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 12, padding: "12px 16px", marginBottom: 12 }}>
            <p style={{ margin: "0 0 10px", fontSize: 13, color: "#fca5a5" }}>
              Permanently delete booking <strong style={{ color: "#ef4444" }}>{booking.bookingId}</strong>? This cannot be undone.
            </p>
            {deleteError && <p style={{ margin: "0 0 8px", fontSize: 12, color: "#f87171" }}>{deleteError}</p>}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="bp-btn primary"
                style={{ background: "#ef4444", boxShadow: "none" }}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting…" : "Yes, Delete"}
              </button>
              <button className="bp-btn secondary" onClick={() => setConfirmDelete(false)}>Back</button>
            </div>
          </div>
        )}

        <div className="bp-modal-footer">
          <button
            className="bp-btn secondary"
            style={{ background: "rgba(201,168,76,0.12)", border: "1px solid rgba(201,168,76,0.15)", color: "#C9A84C" }}
            onClick={() => onEdit(booking)}
          >
            <FontAwesomeIcon icon={faPen} /> Edit
          </button>
          {!isCancelled && !cancelOpen && !confirmDelete && (
            <button
              className="bp-btn secondary"
              style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)", color: "#92400e" }}
              onClick={() => setCancelOpen(true)}
            >
              Cancel Booking
            </button>
          )}
          {!confirmDelete && !cancelOpen && (
            <button
              className="bp-btn secondary"
              style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)", color: "#f87171" }}
              onClick={() => setConfirmDelete(true)}
            >
              <FontAwesomeIcon icon={faTrash} /> Delete
            </button>
          )}
          <button className="bp-btn secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function BookingsPage() {
  usePricingConfig();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [toast, setToast] = useState("");
  const [guestsList, setGuestsList] = useState<GuestOption[]>([]);
  const [roomsList, setRoomsList] = useState<RoomOption[]>([]);
  const [seasonsList, setSeasonsList] = useState<{ id: number; season: string; start_date: string; end_date: string }[]>([]);

  // Add/Edit form
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"add" | "edit">("add");
  const [formInitial, setFormInitial] = useState<BookingForm & { bookingId?: string }>({ ...emptyBookingForm });

  const token = localStorage.getItem("ai2_token");
  const authHeaders = { Authorization: `Bearer ${token}` };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const fetchBookings = useCallback(() => {
    setLoading(true);
    axios
      .get(`${API_BASE}/api/dashboard/bookings`, { headers: authHeaders })
      .then(res => {
        setBookings(res.data.bookings || []);
        setAnalytics(res.data.analytics || null);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Fetch guests + rooms for dropdowns
  useEffect(() => {
    axios.get(`${API_BASE}/api/dashboard/guests`, { headers: authHeaders })
      .then(res => {
        if (Array.isArray(res.data)) {
          setGuestsList(res.data.map((g: any) => ({
            id: g.id, name: g.name, phoneNumber: String(g.phoneNumber ?? ""), email: g.email,
          })));
        }
      }).catch(console.error);

    axios.get(`${API_BASE}/api/rooms`, { headers: authHeaders })
      .then(res => {
        if (Array.isArray(res.data)) {
          setRoomsList(res.data.map((r: any) => ({
            roomId: r.roomid,
            roomNumber: String(r.roomnumber ?? r.roomid),
            roomType: r.roomtype ?? "—",
            status: r.status ?? "available",
          })));
        }
      }).catch(console.error);

    axios.get(`${API_BASE}/api/seasons`, { headers: authHeaders })
      .then(res => {
        if (Array.isArray(res.data)) {
          setSeasonsList(res.data.map((s: any) => ({
            id: s.id,
            season: s.Season ?? s.season ?? "",
            start_date: s.start_date ?? "",
            end_date: s.end_date ?? "",
          })));
        }
      }).catch(console.error);
  }, []);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return bookings.filter(b => {
      const matchSearch =
        (b.bookingId || "").toLowerCase().includes(q) ||
        (b.guestName || "").toLowerCase().includes(q) ||
        (b.roomId || "").toLowerCase().includes(q) ||
        ((b as any).roomNumber || "").includes(q) ||
        (b.phone || "").includes(q);
      const displayStatus = (b.effectiveStatus || b.bookingRoomStatus || b.status || "").toLowerCase();
      const matchStatus =
        statusFilter === "all" ? true
        : statusFilter === "on-hold" ? displayStatus.includes("hold")
        : statusFilter === "pending" ? displayStatus.startsWith("pending")
        : displayStatus === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [bookings, search, statusFilter]);

  const statusOptions = ["all", "confirmed", "checked-in", "on-hold", "pending", "checked-out", "cancelled"];
  const statusLabels: Record<string, string> = { "on-hold": "On Hold", "checked-in": "In House", "checked-out": "Checked Out" };

  const pieData = analytics
    ? Object.entries(analytics.statusCounts).map(([name, value], i) => ({ name, value, color: PIE_COLORS[i % PIE_COLORS.length] }))
    : [];

  const threeMonthStats = useMemo(() => {
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 3);
    const cutoffStr = cutoff.toISOString().slice(0, 10);
    const thisMonthKey = new Date().toISOString().slice(0, 7);
    const recent = bookings.filter(b => b.checkIn && b.checkIn.slice(0, 10) >= cutoffStr);
    const confirmedTotal = recent
      .filter(b => {
        const es = (b.effectiveStatus || b.status || "").toLowerCase();
        return ["confirmed", "checked-in", "checked-out"].includes(es);
      })
      .reduce((s, b) => s + (b.total || 0), 0);
    const cancelledTotal = recent
      .filter(b => b.status.toLowerCase() === "cancelled")
      .reduce((s, b) => s + (b.total || 0), 0);
    const thisMonthTotal = bookings
      .filter(b => {
        const es = (b.effectiveStatus || b.status || "").toLowerCase();
        return b.checkIn?.slice(0, 7) === thisMonthKey &&
          ["confirmed", "checked-in", "checked-out"].includes(es);
      })
      .reduce((s, b) => s + (b.total || 0), 0);
    return { confirmedTotal, cancelledTotal, thisMonthTotal };
  }, [bookings]);

  const openAddModal = () => {
    const today = new Date().toISOString().slice(0, 10);
    const activeSeason = seasonsList.find(s => s.start_date <= today && today <= s.end_date);
    setFormMode("add");
    setFormInitial({ ...emptyBookingForm, checkIn: today, season: activeSeason?.season ?? "" });
    setFormOpen(true);
  };

  const openEditModal = async (booking: Booking) => {
    setSelectedBooking(null);
    setFormMode("edit");

    // Load all rooms for this booking (multi-room support)
    let roomIds: string[] = booking.roomId ? [booking.roomId] : [];
    try {
      const brRes = await axios.get(`${API_BASE}/api/bookings/${booking.bookingId}/rooms`, { headers: authHeaders });
      if (Array.isArray(brRes.data) && brRes.data.length > 0) {
        roomIds = brRes.data.map((r: any) => r.roomid || r.roomId).filter(Boolean);
      }
    } catch { /* fallback to single roomId */ }

    setFormInitial({
      bookingId: booking.bookingId,
      guestName: booking.guestName ?? "",
      phone: booking.phone ?? "",
      email: "",
      isVip: false,
      roomId: roomIds[0] ?? booking.roomId ?? "",
      roomIds,
      checkIn: booking.checkIn ? String(booking.checkIn).slice(0, 10) : "",
      checkOut: booking.checkOut ? String(booking.checkOut).slice(0, 10) : "",
      nights: booking.nights ?? 1,
      total: booking.total ?? 0,
      status: booking.status ?? "confirmed",
      guestCount: booking.guestCount ?? 1,
      childrenCount: booking.childrenCount ?? 0,
      dayUse: booking.dayUse ?? false,
      season: booking.season ?? "",
      source: booking.source ?? "direct",
      notes: booking.notes ?? "",
      earlyCheckin: false,
      lateCheckout: false,
      bookingType: (booking.bookingType as any) ?? "Standard",
    });
    setFormOpen(true);
  };

  const handleBookingSaved = (saved: Booking) => {
    setFormOpen(false);
    if (formMode === "add") {
      setBookings(prev => [saved, ...prev]);
      showToast("Booking added successfully.");
    } else {
      setBookings(prev => prev.map(b => b.bookingId === saved.bookingId ? saved : b));
      showToast("Booking updated successfully.");
    }
    fetchBookings();
    axios.get(`${API_BASE}/api/dashboard/guests`, { headers: authHeaders })
      .then(res => {
        if (Array.isArray(res.data))
          setGuestsList(res.data.map((g: any) => ({ id: g.id, name: g.name, phoneNumber: g.phoneNumber, email: g.email })));
      }).catch(() => {});
  };

  const handleBookingDeleted = (bookingId: string) => {
    setSelectedBooking(null);
    setBookings(prev => prev.filter(b => b.bookingId !== bookingId));
    showToast("Booking deleted.");
    fetchBookings();
  };

  return (
    <DashboardLayout>
      <div className="bp-root">

        {/* ── HEADER ── */}
        <div className="bp-header">
          <div className="bp-header-left">
            <h1>Bookings</h1>
            <p>
              {loading ? "Loading…" : `${analytics?.total ?? 0} total · ${analytics?.checkedIn ?? 0} in-house`}
            </p>
          </div>
          <div className="bp-header-actions">
            <button className="bp-btn primary" onClick={openAddModal}>
              <FontAwesomeIcon icon={faPlus} /> Add Booking
            </button>
            <button className="bp-btn secondary" onClick={() => exportBookingsCSV(filtered)}>
              <FontAwesomeIcon icon={faDownload} /> Export CSV
            </button>
          </div>
        </div>

        {/* ── ANALYTICS STRIP ── */}
        {!loading && analytics && (
          <div className="bp-analytics">
            <div className="bp-stat">
              <div className="bp-stat-icon" style={{ background: "rgba(201,168,76,0.12)", color: "#C9A84C" }}>
                <FontAwesomeIcon icon={faCalendarCheck} />
              </div>
              <span className="bp-stat-value">{analytics.total}</span>
              <span className="bp-stat-label">Total Bookings</span>
            </div>
            <div className="bp-stat">
              <div className="bp-stat-icon" style={{ background: "rgba(16,185,129,0.12)", color: "#34d399" }}>
                <FontAwesomeIcon icon={faBed} />
              </div>
              <span className="bp-stat-value">{analytics.checkedIn}</span>
              <span className="bp-stat-label">In House</span>
            </div>
            <div className="bp-stat">
              <div className="bp-stat-icon" style={{ background: "rgba(16,185,129,0.12)", color: "#34d399" }}>
                <FontAwesomeIcon icon={faWallet} />
              </div>
              <span className="bp-stat-value" style={{ fontSize: 15 }}>
                EGP {threeMonthStats.confirmedTotal.toLocaleString()}
              </span>
              <span className="bp-stat-label">Confirmed Revenue (3 Months)</span>
            </div>
            <div className="bp-stat">
              <div className="bp-stat-icon" style={{ background: "rgba(239,68,68,0.12)", color: "#f87171" }}>
                <FontAwesomeIcon icon={faXmark} />
              </div>
              <span className="bp-stat-value" style={{ fontSize: 15 }}>
                EGP {threeMonthStats.cancelledTotal.toLocaleString()}
              </span>
              <span className="bp-stat-label">Cancelled Revenue (3 Months)</span>
            </div>
            <div className="bp-stat">
              <div className="bp-stat-icon" style={{ background: "rgba(201,168,76,0.12)", color: "#C9A84C" }}>
                <FontAwesomeIcon icon={faWallet} />
              </div>
              <span className="bp-stat-value" style={{ fontSize: 15 }}>
                EGP {threeMonthStats.thisMonthTotal.toLocaleString()}
              </span>
              <span className="bp-stat-label">Confirmed Revenue This Month</span>
            </div>
            <div className="bp-stat">
              <div className="bp-stat-icon" style={{ background: "rgba(245,158,11,0.12)", color: "#fbbf24" }}>
                <FontAwesomeIcon icon={faArrowRightToBracket} />
              </div>
              <span className="bp-stat-value">{analytics.arrivalsToday}</span>
              <span className="bp-stat-label">Arrivals Today</span>
            </div>
            <div className="bp-stat">
              <div className="bp-stat-icon" style={{ background: "rgba(249,115,22,0.12)", color: "#fb923c" }}>
                <FontAwesomeIcon icon={faArrowRightFromBracket} />
              </div>
              <span className="bp-stat-value">{analytics.departuresToday}</span>
              <span className="bp-stat-label">Departures Today</span>
            </div>
            <div className="bp-stat">
              <div className="bp-stat-icon" style={{ background: "rgba(239,68,68,0.12)", color: "#f87171" }}>
                <FontAwesomeIcon icon={faXmark} />
              </div>
              <span className="bp-stat-value">{analytics.cancelled}</span>
              <span className="bp-stat-label">Cancelled</span>
            </div>
            <div className="bp-stat">
              <div className="bp-stat-icon" style={{ background: "rgba(6,182,212,0.12)", color: "#22d3ee" }}>
                <FontAwesomeIcon icon={faBed} />
              </div>
              <span className="bp-stat-value">{analytics.avgNights}</span>
              <span className="bp-stat-label">Avg Nights</span>
            </div>
            <div className="bp-stat">
              <div className="bp-stat-icon" style={{ background: "rgba(201,168,76,0.12)", color: "#c9a84c" }}>
                <FontAwesomeIcon icon={faWallet} />
              </div>
              <span className="bp-stat-value" style={{ fontSize: 16 }}>
                EGP {analytics.avgTotal.toLocaleString()}
              </span>
              <span className="bp-stat-label">Avg Booking Value</span>
            </div>
          </div>
        )}

        {/* ── CHARTS ── */}
        {!loading && analytics && (
          <div className="bp-charts">
            {/* Revenue 30 days */}
            <div className="bp-chart-card">
              <p className="bp-chart-title">Revenue Last 30 Days</p>
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={analytics.rev30}>
                  <defs>
                    <linearGradient id="bpRevGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid {...GRID} />
                  <XAxis dataKey="date" tick={TICK} tickLine={false} axisLine={false} interval={6} />
                  <YAxis tick={TICK} tickLine={false} axisLine={false} />
                  <Tooltip {...CHART_TOOLTIP} formatter={(v: number) => [`EGP ${Number(v).toLocaleString()}`, "Revenue"]} />
                  <Area type="monotone" dataKey="revenue" stroke="#10b981" fill="url(#bpRevGrad)" strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Monthly bookings */}
            <div className="bp-chart-card">
              <p className="bp-chart-title">Bookings per Month (Last 6)</p>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={analytics.monthly} barSize={18}>
                  <CartesianGrid {...GRID} />
                  <XAxis dataKey="key" tick={TICK} tickLine={false} axisLine={false} />
                  <YAxis tick={TICK} tickLine={false} axisLine={false} />
                  <Tooltip {...CHART_TOOLTIP} formatter={(v: number) => [`${v}`, "Bookings"]} />
                  <Bar dataKey="bookings" fill="#1a3a5c" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Status pie */}
            <div className="bp-chart-card">
              <p className="bp-chart-title">Booking Status Breakdown</p>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" cx="50%" cy="50%" innerRadius={45} outerRadius={72} paddingAngle={3}>
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    {...CHART_TOOLTIP}
                    formatter={(v: number, _: string, p: { payload?: { name: string } }) => [`${v} bookings`, p.payload?.name ?? ""]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: 6 }}>
                {pieData.map((d, i) => (
                  <span key={i} style={{ fontSize: 11, color: d.color, display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: d.color, display: "inline-block" }} />
                    {d.name} ({d.value})
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── CONTROLS ── */}
        <div className="bp-controls">
          <div className="bp-search-wrap">
            <FontAwesomeIcon icon={faSearch} className="bp-search-icon" />
            <input
              className="bp-search-input"
              type="text"
              placeholder="Search by guest, booking ID, room, phone…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className="bp-search-clear" onClick={() => setSearch("")}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            )}
          </div>
          {statusOptions.map(s => (
            <button
              key={s}
              className={`bp-filter-btn ${statusFilter === s ? "active" : ""}`}
              onClick={() => setStatusFilter(s)}
            >
              {s === "all"
                ? <><FontAwesomeIcon icon={faFilter} /> All</>
                : (statusLabels[s] ?? s)}
            </button>
          ))}
        </div>

        {/* ── TABLE ── */}
        <div className="bp-table-wrap">
          <table className="bp-table">
            <thead>
              <tr>
                <th>Booking ID</th>
                <th>Guest</th>
                <th>Room</th>
                <th>Dates</th>
                <th>Nights</th>
                <th>Total</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? [...Array(8)].map((_, i) => (
                    <tr key={i} className="bp-skeleton-row">
                      {[...Array(8)].map((_, j) => (
                        <td key={j}><div className="bp-skeleton-cell" style={{ width: j === 0 ? "80%" : "60%" }} /></td>
                      ))}
                    </tr>
                  ))
                : filtered.length === 0
                ? (
                  <tr>
                    <td colSpan={8}>
                      <div className="bp-empty">
                        <div className="bp-empty-icon"><FontAwesomeIcon icon={faCalendarCheck} /></div>
                        <p>No bookings found</p>
                      </div>
                    </td>
                  </tr>
                )
                : filtered.map(b => {
                    const displayStatus = b.effectiveStatus || b.bookingRoomStatus || b.status;
                    const st = statusStyle(displayStatus);
                    return (
                      <tr key={b.id} onClick={() => setSelectedBooking(b)}>
                        <td><span className="bp-cell-id">{b.bookingId}</span></td>
                        <td>
                          <div className="bp-cell-guest">{b.guestName}</div>
                          <div className="bp-cell-sub">{b.phone || "—"}</div>
                        </td>
                        <td>
                          {b.roomId
                            ? <span className="bp-room-badge">Room {b.roomId}</span>
                            : <span style={{ fontSize: 10, fontWeight: 700, color: "#f87171", background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)", borderRadius: 99, padding: "2px 8px" }}>No Room</span>}
                        </td>
                        <td>
                          <div>{fmt(b.checkIn)}</div>
                          <div className="bp-cell-sub">{fmt(b.checkOut)}</div>
                        </td>
                        <td>
                          {b.dayUse
                            ? <span style={{ fontSize: 11, background: "#fef3c7", color: "#92400e", borderRadius: 20, padding: "2px 8px", fontWeight: 700 }}>Day Use</span>
                            : b.nights}
                          {(b.childrenCount ?? 0) > 0 && (
                            <div className="bp-cell-sub">{b.childrenCount} child{(b.childrenCount ?? 0) !== 1 ? "ren" : ""}</div>
                          )}
                        </td>
                        <td><span className="bp-total">EGP {b.total.toLocaleString()}</span></td>
                        <td>
                          <span className="bp-status-badge" style={st}>
                            {displayStatus}
                          </span>
                        </td>
                        <td>
                          <button
                            className="bp-view-btn"
                            onClick={e => { e.stopPropagation(); setSelectedBooking(b); }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })
              }
            </tbody>
          </table>
          {!loading && (
            <div className="bp-table-footer">
              {filtered.length} booking{filtered.length !== 1 ? "s" : ""}
              {statusFilter !== "all" || search ? ` (filtered from ${bookings.length})` : ""}
            </div>
          )}
        </div>

      </div>

      {/* ── BOOKING DETAIL MODAL ── */}
      {selectedBooking && (
        <BookingModal
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
          onEdit={openEditModal}
          onDelete={handleBookingDeleted}
        />
      )}

      {/* ── ADD/EDIT FORM MODAL ── */}
      {formOpen && (
        <BookingFormModal
          mode={formMode}
          initial={formInitial}
          guests={guestsList}
          rooms={roomsList}
          seasons={seasonsList}
          allBookings={bookings}
          onClose={() => setFormOpen(false)}
          onSaved={handleBookingSaved}
          authHeaders={authHeaders}
        />
      )}

      {/* ── TOAST ── */}
      {toast && (
        <div style={{
          position: "fixed",
          bottom: 28,
          right: 28,
          background: "rgba(16,185,129,0.15)",
          border: "1px solid rgba(16,185,129,0.3)",
          color: "#34d399",
          padding: "12px 20px",
          borderRadius: 12,
          fontSize: 14,
          fontWeight: 600,
          zIndex: 99999,
          display: "flex",
          alignItems: "center",
          gap: 8,
          backdropFilter: "blur(12px)",
          boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
        }}>
          <FontAwesomeIcon icon={faCheck} />
          {toast}
        </div>
      )}

    </DashboardLayout>
  );
}
